<template>
    <FormsCard>
        <router-link to="/auth" class="auth-back-link">
            <font-awesome-icon icon="fa-arrow-left"/>
            {{ $t('NewPasswordComponent.backToLogin') }}
        </router-link>
        <img class="block mx-auto mb-6 w-32 sm:w-40 h-auto" src="@/assets/omind_logo.png" alt="test">
        <h2 class="text-center pb-5 text-xl sm:text-2xl">
            {{ $t('NewPasswordComponent.headingEnterNewPassword') }}</h2>
        <Form :validateOnValueUpdate="false" :resolver="resolver"
            :validateOnBlur="true" @submit="setNewPassword"
            class="flex flex-col gap-4 w-full md:shrink-0">
            <FormField v-slot="$field" as="section" name="newPassword"
                initialValue="" class="flex flex-col gap-1">
                <Password type="text" :feedback="false" toggleMask fluid size="large"
                    :invalid="$field?.invalid"
                    :placeholder="$t('NewPasswordComponent.placeholders.enterNewPassword')"/>
                <Message v-if="$field?.invalid" severity="error" size="small"
                    variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
            <FormField v-slot="$field" as="section" name="repeatNewPassword"
                initialValue="" class="flex flex-col gap-1">
                <Password type="text" :feedback="false" fluid size="large"
                    :invalid="$field?.invalid"
                    :placeholder="$t('NewPasswordComponent.placeholders.repeatNewPassword')"/>
                <Message v-if="$field?.invalid" severity="error" size="small"
                    variant="simple">{{ $field.error?.message }}</Message>
            </FormField>
            <div class="flex flex-row">
                <LanguageComponent class="float-left mt-1 absolute"/>
            </div>
            <Button type="submit" :disabled="isSubmitting"
                class="btn-primary-accent mt-8 sm:mt-12 w-full sm:w-auto px-6 py-2.5 sm:px-12 sm:py-3 text-base sm:text-xl mx-auto block text-center" unstyled>
                {{ $t('NewPasswordComponent.changePassword') }}
            </Button>
        </Form>
    </FormsCard>
</template>

<script>
import LanguageComponent from '@/components/global/LanguageComponent.vue';
import FormsCard from '../global/slots/FormsCard.vue';
import { Form, FormField } from '@primevue/forms';
import Message from 'primevue/message';
import Password from 'primevue/password';
import Button from 'primevue/button';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from 'zod';

export default {
  name: 'NewPasswordComponent',
  props: ['email', 'validationKey'],
  components: {
    LanguageComponent,
    FormsCard,
    Form,
    FormField,
    Message,
    Password,
    Button,
  },
  data() {
    return {
      isSubmitting: false,
      resolver: zodResolver(
        z.object({
          newPassword: z.string().min(8, 'Password must be at least 8 characters').max(100, 'Password must be at most 100 characters'),
          repeatNewPassword: z.string().min(8, 'Password must be at least 8 characters').max(100, 'Password must be at most 100 characters'),
        }).superRefine(({ newPassword, repeatNewPassword }, ctx) => {
          if (newPassword !== repeatNewPassword) {
            ctx.addIssue({
              path: ['repeatNewPassword'],
              message: 'Passwords do not match',
            });
          }
        }),
      ),
    };
  },
  methods: {
    async setNewPassword(values) {
      if (values.valid) {
        try {
          this.isSubmitting = true;
          const data = {
            email: this.email,
            password: values.values.newPassword,
            validationKey: this.validationKey,
          };
          await this.$store.dispatch('setNewPassword', data);
          this.isSubmitting = false;
          this.$store.commit('setNewPasswordSetSuccesful', true);
          this.$router.push('/auth/new-password-success');
        } catch (error) {
          const errorMessage = error.response.data.message;
          const errorCode = error.response.data.code;
          let toastErrorMessage;
          switch (errorCode) {
            case 40008:
              toastErrorMessage = this.$t(
                'PasswordResetComponent.toast.invalidResetPasswordInformation',
              );
              break;
            case 40004:
              toastErrorMessage = this.$t(
                'PasswordResetComponent.toast.userNotFound',
                { param_name: 'E-Mail', param_email: values.email },
              );
              break;
            default:
              toastErrorMessage = errorMessage;
          }
          this.isSubmitting = false;
          this.$toast.add({
            severity: 'error',
            summary: toastErrorMessage,
            detail: '',
            life: 3000,
          });
        }
      }
    },
  },
  mounted() {
    if (!this.validationKey || !this.email) {
      this.$router.push('not-found');
    }
  },
};
</script>
